/**
 * AmbassadorApplyTile — a shiny, attention-grabbing recruitment tile shown to
 * logged-in shoppers on the home page and their dashboard.
 *
 * States:
 *   • eligible (customer / affiliate, no application) → shimmering "Apply" CTA
 *     that opens <AmbassadorApplyModal/>
 *   • pending application → calmer "we've got it, check your email" tile
 *   • rejected → a gentle "apply again" tile
 *   • ambassador / admin → renders nothing
 *
 * Self-contained: reads auth from context and fetches the caller's application
 * status once. Renders null until it knows it should show, so it never flashes
 * for signed-out visitors or existing ambassadors.
 */
import { useState, useEffect } from 'react';
import { useAuthCtx } from '../App';
import api from '../utils/api';
import AmbassadorApplyModal from './AmbassadorApplyModal';
import { Icon } from './ui/Icon';

export default function AmbassadorApplyTile({ className = '' }) {
  const { user } = useAuthCtx();
  const [status, setStatus] = useState(undefined); // undefined = loading, null = none
  const [showModal, setShowModal] = useState(false);

  const eligibleRole = user && !['ambassador', 'admin'].includes(user.role);

  useEffect(() => {
    if (!eligibleRole) return;
    let alive = true;
    api.get('/ambassadors/application')
      .then((res) => { if (alive) setStatus(res.data.application?.status || null); })
      .catch(() => { if (alive) setStatus(null); });
    return () => { alive = false; };
  }, [eligibleRole]);

  // Don't render for signed-out users, ambassadors, admins, or while loading.
  if (!eligibleRole || status === undefined) return null;

  // ── Pending: reassure + point at their inbox ──────────────────────────────
  if (status === 'pending') {
    return (
      <div className={'rounded-3xl border border-tea-200 bg-tea-50 p-5 sm:p-6 flex items-center gap-4 ' + className}>
        <div className="grid place-items-center h-12 w-12 rounded-2xl bg-white text-tea-600 shrink-0 shadow-sm"><Icon name="mailbox" className="w-6 h-6" /></div>
        <div>
          <div className="font-bold text-gray-900">Your ambassador application is in review</div>
          <p className="text-sm text-gray-600 mt-0.5">
            Thanks for applying! <strong>Check your email</strong> — an admin will review it and let
            you know shortly.
          </p>
        </div>
      </div>
    );
  }

  // ── Eligible (none) or rejected (apply again) → the shiny CTA ──────────────
  const rejected = status === 'rejected';
  return (
    <>
      <div className={'amb-shine relative overflow-hidden rounded-3xl p-6 sm:p-7 text-white shadow-xl ' + className}
        style={{ background: 'linear-gradient(120deg,#ec4899 0%,#be185d 50%,#7a1746 100%)' }}>
        {/* decorative glow + sparkles */}
        <div className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-white/15 blur-2xl" />
        <div className="pointer-events-none absolute right-6 top-5 text-2xl opacity-70 select-none">✦</div>
        <div className="pointer-events-none absolute right-16 bottom-6 text-base opacity-50 select-none">✦</div>

        <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em]">
              ★ Brand Ambassador Program
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold mt-3 leading-tight">
              {rejected ? 'Ready to try again?' : 'Get paid to share the glow.'}
            </h3>
            <p className="text-white/85 text-sm mt-1.5 max-w-md">
              Sell what you already love and earn <strong>6% referral cash your first year</strong>
              {' '}across three levels. Apply in 60 seconds — we just need your socials.
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="shrink-0 inline-flex items-center justify-center rounded-full bg-white text-tea-700 font-bold px-7 py-3.5 text-sm hover:bg-tea-50 transition-colors shadow-lg">
            {rejected ? 'Apply again' : 'Apply now →'}
          </button>
        </div>
      </div>

      {showModal && (
        <AmbassadorApplyModal
          onClose={() => setShowModal(false)}
          onApplied={() => setStatus('pending')}
        />
      )}
    </>
  );
}
